<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="720">
    <v-card class="ajuda-mensagem-card mx-auto">
      <div class="ajuda-header">
        <div class="ajuda-header-main">
          <div>
            <v-icon color="white">mdi-message-text-outline</v-icon>
          </div>
          <div class="ajuda-header-text">
            <h2 class="ajuda-title">
              Como usar a mensagem de confirmação
            </h2>
            <p class="ajuda-subtitle">
              Personalize o texto enviado ao paciente quando você gerar o link de confirmação da consulta.
            </p>
          </div>
        </div>
      </div>
      <v-card-text class="ajuda-body mt-3">
        <section class="ajuda-section">
          <h3 class="ajuda-section-title">1. Campos automáticos</h3>
          <p class="ajuda-section-text">
            Use os campos abaixo dentro da mensagem. O sistema substitui cada campo pelos dados do agendamento
            (data e hora da consulta marcada para o paciente):
          </p>

          <v-simple-table dense class="ajuda-table">
            <tbody>
              <tr>
                <td class="campo" v-pre>{{paciente.Nome}}</td>
                <td>Nome completo do paciente</td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{medico.Nome}}</td>
                <td>Nome completo do médico responsável pela agenda</td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{clinica.Nome}}</td>
                <td>Razão social da clínica</td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{data}}</td>
                <td>Data do agendamento da consulta (ex.: 10/02/2026)</td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{hora}}</td>
                <td>Hora do agendamento da consulta (ex.: 15:20)</td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{link}}</td>
                <td>
                  Link curto para o paciente confirmar ou recusar a consulta.
                  <strong>Obrigatório</strong> para que o paciente consiga confirmar.
                </td>
              </tr>
              <tr>
                <td class="campo" v-pre>{{agenda.Descricao}}</td>
                <td>Descrição da agenda (ex.: Agenda do Dr. João)</td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>

        <section class="ajuda-section">
          <h3 class="ajuda-section-title">2. Exemplo pronto</h3>
          <v-alert dense outlined type="info" class="ajuda-exemplo" border="left">
            <span v-pre>Oi {{paciente.Nome}}! Sua consulta com {{medico.Nome}} na {{clinica.Nome}} dia {{data}} às {{hora}}. Confirme aqui: {{link}}</span>
          </v-alert>
          <p class="ajuda-section-text">
            Você pode adaptar o texto acima para o estilo da sua clínica. Lembre-se de manter o campo
            <span class="campo-inline" v-pre>{{link}}</span> para que o paciente tenha onde clicar.
          </p>
        </section>

        <section class="ajuda-section">
          <h3 class="ajuda-section-title">3. Boas práticas</h3>
          <ul class="ajuda-lista">
            <li>Seja claro e direto: informe data, horário e local da consulta.</li>
            <li>Evite textos muito longos para não quebrar o layout em apps como WhatsApp</li>
            <li>Use uma linguagem amigável e alinhada ao perfil da clínica.</li>
          </ul>
          <p class="ajuda-dica">
            A mensagem pode ter até <strong>300 caracteres</strong> (sem contar a substituição dos campos).
          </p>
        </section>
      </v-card-text>
      <v-card-actions class="ajuda-actions d-flex justify-end">
        <v-btn color="success" type="submit" @click="$emit('input', false)">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'AjudaMensagemConfirmacaoModal',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
});
</script>

<style scoped>
.ajuda-mensagem-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 18px 45px rgba(0, 0, 0, 0.18),
    0 6px 18px rgba(0, 0, 0, 0.08);
}

.ajuda-header {
  background: var(--especialidade-gradient, linear-gradient(135deg, #2e4b9d 0%, #4a6fa5 100%));
  padding: 20px 28px;
  color: #fff;
}

.ajuda-header-main {
  display: flex;
  align-items: center;
  gap: 16px;
}


.ajuda-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ajuda-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.ajuda-subtitle {
  margin: 0;
  font-size: 13px;
  opacity: 0.9;
}

.ajuda-body {
  padding: 20px 28px 8px;
}

.ajuda-section {
  margin-bottom: 18px;
}

.ajuda-section-title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--especialidade-primary, #2e4b9d);
}

.ajuda-section-text {
  margin: 0 0 8px;
  font-size: 13px;
  color: #555;
}

.ajuda-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.ajuda-table td {
  font-size: 13px;
  padding: 6px 10px;
}

.ajuda-table tr:nth-child(odd) {
  background: #fafbff;
}

.campo {
  white-space: nowrap;
  font-family: 'Fira Code', monospace;
  color: var(--especialidade-primary, #2e4b9d);
}

.campo-inline {
  font-family: 'Fira Code', monospace;
  color: var(--especialidade-primary, #2e4b9d);
}

.ajuda-exemplo {
  font-size: 13px;
  border-left: 4px solid var(--especialidade-primary, #2e4b9d) !important;
}

.ajuda-lista {
  margin: 0 0 6px 16px;
  padding: 0;
  font-size: 13px;
  color: #555;
}

.ajuda-lista li {
  margin-bottom: 4px;
}

.ajuda-dica {
  margin: 0;
  font-size: 12px;
  color: #777;
}

.ajuda-actions {
  padding: 10px 24px 16px;
}
</style>
